import Cookies from 'js-cookie';
import { isSuperAdminEmail } from '@utils/access';

export interface ImpersonationSession {
  id: number;
  impersonatorId: number;
  targetUserId: number;
  startedAt: string;
  reason?: string;
}

export interface StartImpersonationResponse {
  success: boolean;
  data: {
    id: number;
    email: string;
    name?: string;
    accessToken: string;
    refreshToken: string;
    ppjkNpwp?: string;
    [key: string]: any;
  };
  message: string;
}

export interface User {
  id: number;
  email: string;
  name?: string;
}

const IMPERSONATION_SESSION_KEY = 'impersonationSession';
const ORIGINAL_TOKENS_KEY = 'originalTokens';

export const impersonationUtils = {
  // Check if user can impersonate (super-admin accounts only)
  canImpersonate: (email: string): boolean => {
    return isSuperAdminEmail(email);
  },

  // Check if currently impersonating
  isImpersonating: (): boolean => {
    return localStorage.getItem(IMPERSONATION_SESSION_KEY) !== null;
  },

  // Get current impersonation session info
  getImpersonationInfo: (): ImpersonationSession | null => {
    const session = localStorage.getItem(IMPERSONATION_SESSION_KEY);
    return session ? JSON.parse(session) : null;
  },

  // Get impersonated user info
  getImpersonatedUserInfo: (): any => {
    const userInfo = localStorage.getItem('impersonatedUserInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  },

  // Store original tokens before impersonation (from both cookies and localStorage)
  storeOriginalTokens: (): void => {
    // Get tokens from cookies first (priority), fallback to localStorage
    const accessToken = Cookies.get('accessToken') || localStorage.getItem('accessToken');
    const refreshToken = Cookies.get('refreshToken') || localStorage.getItem('refreshToken');
    const nomorPpjk = localStorage.getItem('nomor_ppjk');

    if (accessToken && refreshToken) {
      localStorage.setItem(ORIGINAL_TOKENS_KEY, JSON.stringify({
        accessToken,
        refreshToken,
        nomorPpjk
      }));
    }
  },

  // Start impersonation
  startImpersonation: async (targetUserId: number, reason?: string): Promise<void> => {
    // Get access token from cookies first (priority), fallback to localStorage
    const currentAccessToken = Cookies.get('accessToken') || localStorage.getItem('accessToken');
    const currentAuthData = localStorage.getItem('authData');

    if (!currentAccessToken) {
      throw new Error('No access token found');
    }

    // Store original tokens and auth data before starting impersonation
    impersonationUtils.storeOriginalTokens();
    if (currentAuthData) {
      localStorage.setItem('originalAuthData', currentAuthData);
    }

    const response = await fetch('/api/v1/auth/impersonation/start', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentAccessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        targetUserId,
        reason: reason || ''
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to start impersonation' }));
      throw new Error(error.message || 'Failed to start impersonation');
    }

    const data: StartImpersonationResponse = await response.json();

    // IMPORTANT: Update tokens in BOTH cookies AND localStorage
    // This ensures axios interceptor picks up the impersonated user's token
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    Cookies.set('accessToken', data.data.accessToken, { secure: isSecure, sameSite: 'strict' });
    Cookies.set('refreshToken', data.data.refreshToken, { secure: isSecure, sameSite: 'strict' });
    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);
    localStorage.setItem('authData', JSON.stringify(data.data));

    // Update PPJK NPWP for impersonated user (only if not empty)
    if (data.data.ppjkNpwp && data.data.ppjkNpwp.trim() !== '') {
      localStorage.setItem('nomor_ppjk', data.data.ppjkNpwp);
    } else {
      // Remove nomor_ppjk if impersonated user doesn't have one
      localStorage.removeItem('nomor_ppjk');
    }

    // Mark as impersonating with session info
    const impersonationSession: ImpersonationSession = {
      id: Date.now(), // Use timestamp as session ID
      impersonatorId: 0, // Will be set by JWT claims
      targetUserId: data.data.id,
      startedAt: new Date().toISOString(),
      reason: reason || ''
    };
    localStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(impersonationSession));
    localStorage.setItem('impersonatedUserInfo', JSON.stringify(data.data));

    // Dispatch custom event to notify components of impersonation state change
    window.dispatchEvent(new Event('impersonationChanged'));
  },

  // Stop impersonation
  stopImpersonation: async (): Promise<void> => {
    const session = impersonationUtils.getImpersonationInfo();
    // Get access token from cookies first (priority), fallback to localStorage
    const currentAccessToken = Cookies.get('accessToken') || localStorage.getItem('accessToken');

    if (!session || !currentAccessToken) {
      throw new Error('No active impersonation session');
    }

    try {
      const response = await fetch('/api/v1/auth/impersonation/stop', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentAccessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: session.id
        })
      });

      if (!response.ok) {
        console.warn('Failed to stop impersonation on server, proceeding with cleanup');
      }
    } catch (error) {
      console.warn('Error stopping impersonation on server:', error);
    }

    // Clean up impersonation-related local storage
    localStorage.removeItem(IMPERSONATION_SESSION_KEY);
    localStorage.removeItem('impersonatedUserInfo');

    // Restore original tokens and auth data if available
    const originalTokens = localStorage.getItem(ORIGINAL_TOKENS_KEY);
    const originalAuthData = localStorage.getItem('originalAuthData');

    if (originalTokens && originalAuthData) {
      try {
        const tokens = JSON.parse(originalTokens);
        const authData = JSON.parse(originalAuthData);

        // IMPORTANT: Restore tokens in BOTH cookies AND localStorage
        // This ensures axios interceptor picks up the original user's token
        const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
        Cookies.set('accessToken', tokens.accessToken, { secure: isSecure, sameSite: 'strict' });
        Cookies.set('refreshToken', tokens.refreshToken, { secure: isSecure, sameSite: 'strict' });
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        localStorage.setItem('authData', JSON.stringify(authData));

        // Restore original PPJK NPWP
        if (tokens.nomorPpjk) {
          localStorage.setItem('nomor_ppjk', tokens.nomorPpjk);
        } else {
          localStorage.removeItem('nomor_ppjk');
        }

        localStorage.removeItem(ORIGINAL_TOKENS_KEY);
        localStorage.removeItem('originalAuthData');

        // Dispatch custom event to notify components of impersonation state change
        window.dispatchEvent(new Event('impersonationChanged'));
      } catch (error) {
        console.error('Failed to restore original tokens and auth data:', error);
        // If we can't restore, redirect to login
        window.location.href = '/auth';
        return;
      }
    } else {
      console.error('Missing original data for restoration:', {
        originalTokens: !!originalTokens,
        originalAuthData: !!originalAuthData
      });
      // No original data, redirect to login
      window.location.href = '/auth';
      return;
    }
  },

};